import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useMemo, useState } from 'react'
import { api } from '@/lib/api'
import { getAdminToken } from '@/lib/admin-store'
import {
  applyAdminFilters,
  getAdminNavLinks,
  readAdminFiltersFromSearch,
  type AdminFilters,
} from './-admin-utils'
import styles from './admin.module.css'

export const Route = createFileRoute('/admin/promoters')({
  component: AdminPromotersRoute,
})

function AdminPromotersRoute() {
  const [token] = useState(getAdminToken)
  const [filters, setFilters] = useState<AdminFilters>(readAdminFiltersFromSearch)
  const [rows, setRows] = useState<Awaited<ReturnType<typeof api.adminListPromoters>>['items']>([])
  const [newEmail, setNewEmail] = useState('')
  const [newName, setNewName] = useState('')
  const [newCampaignLabel, setNewCampaignLabel] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const navLinks = useMemo(() => getAdminNavLinks(filters), [filters])
  const promoterStatus =
    filters.status === 'active' || filters.status === 'suspended' || filters.status === 'revoked'
      ? filters.status
      : 'all'

  useEffect(() => {
    const load = async (): Promise<void> => {
      if (!token) {
        return
      }

      setIsLoading(true)
      setError(null)

      try {
        const statusFilter = promoterStatus === 'all' ? undefined : promoterStatus
        const data = await api.adminListPromoters(
          token,
          statusFilter ? { status: statusFilter } : undefined
        )
        setRows(data.items)
      } catch (loadError) {
        setError(loadError instanceof Error ? loadError.message : 'Failed to load promoters.')
      } finally {
        setIsLoading(false)
      }
    }

    void load()
  }, [promoterStatus, token])

  const handleCreatePromoter = async (): Promise<void> => {
    if (!token) {
      return
    }

    if (newEmail.trim().length === 0 || newName.trim().length === 0) {
      setError('Email and display name are required.')
      return
    }

    setError(null)
    setNotice(null)
    setIsLoading(true)

    try {
      const created = await api.adminCreatePromoter(token, {
        email: newEmail.trim(),
        name: newName.trim(),
        ...(newCampaignLabel.trim() ? { campaignLabel: newCampaignLabel.trim() } : {}),
      })
      setNotice(`Promoter created. Invite code: ${created.inviteCode}`)
      setNewEmail('')
      setNewName('')
      setNewCampaignLabel('')
      const refreshed = await api.adminListPromoters(token)
      setRows(refreshed.items)
    } catch (createError) {
      setError(createError instanceof Error ? createError.message : 'Failed to create promoter.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <section className={styles.layout}>
      <header className={styles.hero}>
        <h1 className={styles.title}>Promoters</h1>
        <p className={styles.subtitle}>
          Create and manage promoter accounts with filterable status views.
        </p>
      </header>

      <section className={styles.card}>
        <div className={styles.tabs}>
          {navLinks.map((link) => (
            <a key={link.path} href={link.href} className={styles.tabLink}>
              {link.label}
            </a>
          ))}
        </div>
        <div className={styles.row}>
          <label>
            Status
            <select
              className={styles.select}
              value={promoterStatus}
              onChange={(event) =>
                setFilters((current) => ({
                  ...current,
                  status:
                    event.target.value === 'active' ||
                    event.target.value === 'suspended' ||
                    event.target.value === 'revoked'
                      ? event.target.value
                      : 'all',
                }))
              }
            >
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
              <option value="revoked">Revoked</option>
            </select>
          </label>
          <button
            className={styles.secondaryButton}
            type="button"
            onClick={() => applyAdminFilters(filters)}
          >
            Apply Filters
          </button>
          <button
            className={styles.ghostButton}
            type="button"
            onClick={() =>
              applyAdminFilters({
                ...filters,
                status: 'all',
              })
            }
          >
            Clear Filters
          </button>
        </div>
      </section>

      {!token && (
        <section className={styles.card}>
          <p className={styles.emptyState}>You don't have admin access.</p>
          <a className={styles.tabLink} href="/admin">
            Go to Home
          </a>
        </section>
      )}

      {token && (
        <section className={styles.card}>
          <h2>Create Promoter</h2>
          <div className={styles.row}>
            <input
              className={styles.input}
              type="email"
              placeholder="Email"
              value={newEmail}
              onChange={(event) => setNewEmail(event.target.value)}
            />
            <input
              className={styles.input}
              type="text"
              placeholder="Display name"
              value={newName}
              onChange={(event) => setNewName(event.target.value)}
            />
            <input
              className={styles.input}
              type="text"
              placeholder="Campaign label (optional)"
              value={newCampaignLabel}
              onChange={(event) => setNewCampaignLabel(event.target.value)}
            />
            <button
              className={styles.button}
              type="button"
              onClick={() => void handleCreatePromoter()}
              disabled={isLoading}
            >
              Create Promoter
            </button>
          </div>
          {notice && <p className={styles.notice}>{notice}</p>}
          {error && <p className={styles.error}>{error}</p>}
        </section>
      )}

      {token && !isLoading && rows.length === 0 && !error && (
        <section className={styles.card}>
          <p className={styles.emptyState}>No promoter accounts created.</p>
          <p className={styles.muted}>Use the form above to create your first promoter.</p>
        </section>
      )}

      {token && rows.length > 0 && (
        <section className={styles.card}>
          <h2>Promoter Accounts</h2>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>ID</th>
                <th>Status</th>
                <th>Email</th>
                <th>Credits</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const search = typeof window !== 'undefined' ? window.location.search : ''
                return (
                  <tr key={row.id}>
                    <td>
                      <a
                        className={styles.tabLink}
                        href={`/admin/promoters/${encodeURIComponent(row.id)}${search}`}
                      >
                        {row.id}
                      </a>
                    </td>
                    <td>{row.status}</td>
                    <td>{row.email}</td>
                    <td>{row.availableCredits}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </section>
      )}
    </section>
  )
}
